import type React from 'react';
import { useCallback, useState, useMemo, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { FloatTextArea } from 'ui-components/components/float-text-area';
import type { FloatTextAreaProps } from 'ui-components/components/float-text-area';
import { InlineMessage } from 'ui-components/components/inline-message';
import type { InlineMessageType } from 'ui-components/components/inline-message/types';
import { useYupFormValidation } from '../../validation/use-yup-form-validation';
import { buildSchema } from 'ui-components/components/input/with-character-count/basic-schema';
import { messages } from 'ui-components/components/input/with-character-count/messages';

type FloatTextAreaWithCharacterCountProps = Omit<FloatTextAreaProps, 'hasError' | 'maxLength'> & {
  displayName?: string;
  isNullable?: boolean;
  isRequired?: boolean;
  max: number;
  name: string;
  setHasError?: (name: string, hasError: boolean) => void;
};

export const FloatTextAreaWithCharacterCount = ({
  max,
  name,
  displayName,
  value = '',
  onChange,
  onFocus: onFocusProp,
  setHasError,
  isRequired = false,
  isNullable = false,
  disabled,
  ...rest
}: FloatTextAreaWithCharacterCountProps) => {
  const realDisplayName = displayName ?? name;
  const [touched, setTouched] = useState(false);

  const handleFocus = useCallback(() => {
    setTouched(true);
    onFocusProp?.();
  }, [onFocusProp]);

  const schema = useMemo(
    () =>
      buildSchema({
        isNullable,
        isRequired,
        name: realDisplayName,
        max,
      }),
    [isNullable, isRequired, realDisplayName, max],
  );

  const form = useMemo(() => ({ [realDisplayName]: value }), [realDisplayName, value]);
  const { errors: validationErrors, isValid } = useYupFormValidation({
    form,
    schema,
  });

  useEffect(() => {
    if (touched && setHasError) {
      setHasError(name, !isValid);
    }
  }, [touched, isValid, setHasError, name]);

  const hasError = touched && !isValid;
  const inlineMessageType: InlineMessageType | '' = hasError ? 'error' : '';

  const inlineMessage = useMemo(() => {
    if (!touched) {
      return <FormattedMessage {...messages.maximumCharacters} values={{ max }} />;
    }

    if (hasError) {
      return (validationErrors as Record<string, { message?: React.ReactNode }>)[realDisplayName]?.message;
    }

    return (
      <FormattedMessage
        {...messages.maximumCharactersWithRemaining}
        values={{
          b: (chunks: React.ReactNode) => <b>{chunks}</b>,
          remaining: max - (value?.length ?? 0),
          max,
        }}
      />
    );
  }, [max, touched, hasError, value?.length, validationErrors, realDisplayName]);

  return (
    <div>
      <FloatTextArea {...rest} disabled={disabled} hasError={hasError} onChange={onChange} onFocus={handleFocus} value={value} />
      {inlineMessage && <InlineMessage type={inlineMessageType}>{inlineMessage}</InlineMessage>}
    </div>
  );
};
